import { Spacer } from "@/components/Spacer";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export default function Section06({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="w-full flex desktop:flex-row flex-col items-center desktop:justify-center bg-[#ffffff] desktop:py-[160px] desktop:px-[32px] py-[80px] px-[20px]">
      <motion.div
        id="section-06-container"
        className="desktop:w-[960px] w-[342px] flex desktop:flex-row flex-col desktop:gap-[60px]"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.1,
        }}
      >
        <div className="desktop:w-full desktop:max-w-[379px] flex flex-col desktop:items-start items-center justify-start">
          <div
            className="font-eng-01-semibold text-[#5364F2] leading-[100%] desktop:text-[18px] text-[13px] desktop:text-start text-center"
            style={{ letterSpacing: "-0.02em" }}
          >
            Know What You're Experiencing?
          </div>
          <Spacer height={12} />
          <div
            className="font-pre-semibold text-[#17191C] leading-[130%] desktop:text-[32px] text-[24px] desktop:text-start text-center"
            style={{ letterSpacing: "-0.03em" }}
          >
            Easily book the right clinic
            <br /> for your symptoms
          </div>
          <Spacer height={16} />
          <div
            className="font-eng-regular text-[#2E323A] desktop:leading-[130%] leading-[140%] desktop:text-[15px] text-[14px] desktop:text-start text-center"
            style={{ letterSpacing: "-0.04em" }}
          >
            Get department recommendations based on your <br /> symptoms,and
            find nearby hospitals and pharmacies —<br /> all in one place.
          </div>

          <Spacer height={isMobile ? 16 : 54} />
          <div
            id="section-06-carousel"
            className="desktop:w-[379.5px] w-[342px] desktop:h-[110px] h-[86px] overflow-hidden"
          >
            <Carousel isMobile={isMobile} />
          </div>
          <Spacer height={isMobile ? 32 : 60} />
        </div>

        <div className="w-full flex justify-center items-start">
          <img
            src="assets/images/section-06-02-eng.png"
            alt="section-06-02"
            className="desktop:w-[521px] desktop:h-[350px] w-[342px] h-[230px]"
          />
        </div>
      </motion.div>
    </div>
  );
}

interface CarouselProps {
  isMobile: boolean;
}

type ItemProps = {
  image: string;
  title: string;
};

const Carousel = ({ isMobile }: CarouselProps) => {
  const urlPrefix = "assets/images/subjects/";
  const images: ItemProps[] = isMobile
    ? [
        {
          image: `${urlPrefix}FamilyMedicine-mobile-eng.png`,
          title: "가정의학과",
        },
        { image: `${urlPrefix}InternalMedicine-mobile-eng.png`, title: "내과" },
        {
          image: `${urlPrefix}AnesthesiologyandPainMedicine-mobile-eng.png`,
          title: "마취통증의학과",
        },
        { image: `${urlPrefix}Urology-mobile-eng.png`, title: "비뇨기과" },
        {
          image: `${urlPrefix}ObstetricsandGynecology-mobile-eng.png`,
          title: "산부인과",
        },
        {
          image: `${urlPrefix}PlasticSurgery-mobile-eng.png`,
          title: "성형외과",
        },
        {
          image: `${urlPrefix}Pediatrics-mobile-eng.png`,
          title: "소아청소년과",
        },
        { image: `${urlPrefix}Neurology-mobile-eng.png`, title: "신경과" },
        { image: `${urlPrefix}Neurosurgery-mobile-eng.png`, title: "신경외과" },
        { image: `${urlPrefix}Ophthalmology-mobile-eng.png`, title: "안과" },
        { image: `${urlPrefix}Radiology-mobile-eng.png`, title: "영상의학과" },
        { image: `${urlPrefix}GeneralSurgery-mobile-eng.png`, title: "외과" },
        {
          image: `${urlPrefix}Otorhinolaryngology-mobile-eng.png`,
          title: "이비인후과",
        },
        {
          image: `${urlPrefix}RehabilitationMedicine-mobile-eng.png`,
          title: "재활의학과",
        },
        {
          image: `${urlPrefix}Psychiatry-mobile-eng.png`,
          title: "정신건강의학과",
        },
        {
          image: `${urlPrefix}OrthopedicSurgery-mobile-eng.png`,
          title: "정형외과",
        },
        { image: `${urlPrefix}Dentistry-mobile-eng.png`, title: "치과" },
        { image: `${urlPrefix}Dermatology-mobile-eng.png`, title: "피부과" },
        {
          image: `${urlPrefix}ThoracicSurgery-mobile-eng.png`,
          title: "흉부외과",
        },
      ]
    : [
        {
          image: `${urlPrefix}FamilyMedicine-desktop-eng.png`,
          title: "가정의학과",
        },
        {
          image: `${urlPrefix}InternalMedicine-desktop-eng.png`,
          title: "내과",
        },
        {
          image: `${urlPrefix}AnesthesiologyandPainMedicine-desktop-eng.png`,
          title: "마취통증의학과",
        },
        { image: `${urlPrefix}Urology-desktop-eng.png`, title: "비뇨기과" },
        {
          image: `${urlPrefix}ObstetricsandGynecology-desktop-eng.png`,
          title: "산부인과",
        },
        {
          image: `${urlPrefix}PlasticSurgery-desktop-eng.png`,
          title: "성형외과",
        },
        {
          image: `${urlPrefix}Pediatrics-desktop-eng.png`,
          title: "소아청소년과",
        },
        { image: `${urlPrefix}Neurology-desktop-eng.png`, title: "신경과" },
        {
          image: `${urlPrefix}Neurosurgery-desktop-eng.png`,
          title: "신경외과",
        },
        { image: `${urlPrefix}Ophthalmology-desktop-eng.png`, title: "안과" },
        { image: `${urlPrefix}Radiology-desktop-eng.png`, title: "영상의학과" },
        { image: `${urlPrefix}GeneralSurgery-desktop-eng.png`, title: "외과" },
        {
          image: `${urlPrefix}Otorhinolaryngology-desktop-eng.png`,
          title: "이비인후과",
        },
        {
          image: `${urlPrefix}RehabilitationMedicine-desktop-eng.png`,
          title: "재활의학과",
        },
        {
          image: `${urlPrefix}Psychiatry-desktop-eng.png`,
          title: "정신건강의학과",
        },
        {
          image: `${urlPrefix}OrthopedicSurgery-desktop-eng.png`,
          title: "정형외과",
        },
        { image: `${urlPrefix}Dentistry-desktop-eng.png`, title: "치과" },
        { image: `${urlPrefix}Dermatology-desktop-eng.png`, title: "피부과" },
        {
          image: `${urlPrefix}ThoracicSurgery-desktop-eng.png`,
          title: "흉부외과",
        },
      ];

  // 두 배의 이미지를 생성하여 무한 스크롤 효과 구현
  const duplicatedImages = [...images, ...images];

  const getImageSize = (image: string) => {
    // 이미지 경로에서 파일명만 추출 (경로와 확장자 제거)
    const fileName = image.split("/").pop()?.split("-")[0] || "";

    // 특별한 크기가 필요한 이미지들의 맵 정의
    const specialSizes = {
      AnesthesiologyandPainMedicine: isMobile
        ? { width: 92, height: 86 }
        : { width: 117, height: 110 },
      Pediatrics: isMobile
        ? { width: 79, height: 86 }
        : { width: 101, height: 110 },
      Psychiatry: isMobile
        ? { width: 92, height: 86 }
        : { width: 117, height: 110 },
    };

    // 특별한 크기가 있으면 해당 크기 반환, 없으면 기본 크기 반환
    return (
      specialSizes[fileName as keyof typeof specialSizes] ||
      (isMobile ? { width: 74, height: 86 } : { width: 97, height: 110 })
    );
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* 클리핑 뷰포트 - 높이 제한 */}
      <div className="relative w-full h-full overflow-hidden">
        <div className="absolute top-0 left-0 desktop:h-[326px] h-[386px] w-[15%] z-10 bg-gradient-to-r from-[#FFFFFF] to-transparent pointer-events-none"></div>
        <div className="flex items-center h-full overflow-hidden">
          <motion.div
            className="flex gap-[2px] h-full"
            animate={{
              x: [
                0,
                -(images.length * (isMobile ? 120 : 140) + images.length * 5),
              ],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: images.length * 7,
                ease: "linear",
              },
            }}
          >
            {duplicatedImages.map((image, index) => (
              <motion.div
                key={index}
                className={`h-full flex-shrink-0 overflow-hidden flex items-center justify-center`}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={image.image}
                  alt={image.title}
                  style={{
                    width: `${getImageSize(image.image).width}px`,
                    height: `${getImageSize(image.image).height}px`,
                    objectFit: "contain",
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
        <div className="absolute top-0 right-0 h-full w-[15%] z-10 bg-gradient-to-l from-[#FFFFFF] to-transparent pointer-events-none"></div>
      </div>
    </div>
  );
};
